<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { UserService, type UserProfileUpdateData, type UserUpdateResponse } from '../../lib/api/user.service';
  import { userStore } from '../../stores/auth.store';
  import Button from '../Button.svelte';

  export let isOpen = false;
  export let user: any = null;

  const dispatch = createEventDispatcher();
  
  let loading = false;
  let fieldErrors: { [key: string]: string } = {};
  
  let profileData: UserProfileUpdateData = {
    full_name: '',
    phone: '',
    job_title: '',
    location: ''
  };

  let isInitialized = false;

  // Initialize form data when modal opens
  $: if (isOpen && user && !isInitialized) {
    initializeProfileData();
    isInitialized = true;
  }

  // Reset initialization flag when modal closes
  $: if (!isOpen) {
    isInitialized = false;
  }

  // Validation functions
  function validateRequired(value: string): boolean {
    return !!(value && value.trim().length > 0);
  }

  function validateForm(): boolean {
    fieldErrors = {};
    let isValid = true;

    // Validate required fields
    if (!profileData.full_name || profileData.full_name.trim() === '') {
      fieldErrors.full_name = 'Full name is required';
      isValid = false;
    }

    if (!profileData.phone || profileData.phone.trim() === '') {
      fieldErrors.phone = 'Phone number is required';
      isValid = false;
    }

    // Show error message if validation fails
    if (!isValid) {
      const errorMessages = Object.entries(fieldErrors)
        .map(([field, message]) => `${field.replace('_', ' ')}: ${message}`)
        .join('; ');
      
      dispatch('error', { message: `Please fix the following errors: ${errorMessages}` });
    }

    return isValid;
  }

  function getFieldError(fieldName: string): string | null {
    return fieldErrors[fieldName] || null;
  }

  function handleApiError(error: any, fallbackMessage: string) {
    if (error?.response?.data?.data?.errors) {
      const errors = error.response.data.data.errors;
      Object.keys(errors).forEach(field => {
        fieldErrors[field] = Array.isArray(errors[field]) ? errors[field][0] : errors[field];
      });
    } else if (error?.response?.data?.message) {
      dispatch('error', { message: error.response.data.message });
    } else {
      dispatch('error', { message: fallbackMessage });
    }
  }

  function initializeProfileData() {
    try {
    profileData = {
      full_name: user.full_name || '',
      phone: user.phone || '',
      job_title: user.job_title || '',
      location: user.location || ''
    };
      
      fieldErrors = {}; // Reset errors when opening modal
    } catch (error: any) {
      console.error('Failed to initialize profile data:', error);
    }
  }

  function handleButtonClick() {
    handleSubmit();
  }

  async function handleSubmit() {
    if (!validateForm()) {
      return;
    }

    loading = true;
    fieldErrors = {};
    
    try {
      const result: UserUpdateResponse = await UserService.updateUserProfile(profileData);
      
      if (result.success && result.user) {
        // Update user store with the updated user data
        userStore.set(result.user);
        
        dispatch('success', { 
          message: result.message || 'Profile updated successfully!',
          user: result.user 
        });
        closeModal();
      } else {
        // Handle UserService error response
        if (result.error?.response?.data?.data?.errors) {
          const errors = result.error.response.data.data.errors;
          Object.keys(errors).forEach(field => {
            fieldErrors[field] = Array.isArray(errors[field]) ? errors[field][0] : errors[field];
          });
        }
        dispatch('error', { message: result.message || 'Failed to update profile. Please try again.' });
      }
    } catch (error: any) {
      console.error('Profile update error:', error);
      handleApiError(error, 'Failed to update profile. Please try again.');
    } finally {
      loading = false;
    }
  }

  function closeModal() {
    // Reset form data
    profileData = {
      full_name: '',
      phone: '',
      job_title: '',
      location: ''
    };
    fieldErrors = {};
    dispatch('close');
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      closeModal();
    } else if (event.key === 'Enter' && !loading) {
      handleSubmit();
    }
  }
</script>

{#if isOpen}
  <div class="modal-overlay" role="presentation" on:click|self={closeModal} on:keydown={(e) => e.key === 'Escape' && closeModal()}>
    <div class="modal-content" role="dialog" aria-modal="true" aria-labelledby="employee-edit-profile-title">
      <div class="modal-header">
        <h3 id="employee-edit-profile-title">Edit Employee Profile</h3>
        <button class="modal-close" on:click={closeModal}>×</button>
      </div>
      
      <div class="modal-body">
        <form on:submit|preventDefault={handleSubmit}>
          <div class="form-group">
            <label for="full_name">
              Full Name <span class="required">*</span>
            </label>
            <input
              id="full_name"
              type="text"
              bind:value={profileData.full_name}
              placeholder="Enter your full name"
              class="form-input"
              class:error={getFieldError('full_name')}
              disabled={loading}
            />
            {#if getFieldError('full_name')}
              <div class="field-error">{getFieldError('full_name')}</div>
            {/if}
          </div>
          
          <div class="form-group">
            <label for="phone">
              Phone Number <span class="required">*</span>
            </label>
            <input
              id="phone"
              type="tel"
              bind:value={profileData.phone}
              placeholder="Enter your phone number"
              class="form-input"
              class:error={getFieldError('phone')}
              disabled={loading}
            />
            {#if getFieldError('phone')}
              <div class="field-error">{getFieldError('phone')}</div>
            {/if}
          </div>
          
          <div class="form-group">
            <label for="job_title">
              Job Title
            </label>
            <input
              id="job_title"
              type="text"
              bind:value={profileData.job_title}
              placeholder="Enter your job title"
              class="form-input"
              class:error={getFieldError('job_title')}
              disabled={loading}
            />
            {#if getFieldError('job_title')}
              <div class="field-error">{getFieldError('job_title')}</div>
            {/if}
          </div>
          
          <div class="form-group">
            <label for="location">
              Location
            </label>
            <input
              id="location"
              type="text"
              bind:value={profileData.location}
              placeholder="Enter your work location"
              class="form-input"
              class:error={getFieldError('location')}
              disabled={loading}
            />
            {#if getFieldError('location')}
              <div class="field-error">{getFieldError('location')}</div>
            {/if}
          </div>
        </form>
        
        <div class="info-section">
          <div class="info-card">
            <h4>👤 Personal Information</h4>
            <p>Update your personal and work details. All fields are optional and will be validated by the server.</p>
          </div>
        </div>
      </div>
      
      <div class="modal-footer">
        <Button 
          variant="secondary" 
          on:click={closeModal}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button 
          variant="primary" 
          on:click={handleButtonClick}
          disabled={loading}
        >
          {loading ? 'Updating...' : 'Update Profile'}
        </Button>
      </div>
    </div>
  </div>
{/if}

<style>
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2000;
  }

  .modal-content {
    background-color: #fff;
    border-radius: 12px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
    width: 90%;
    max-width: 550px;
    max-height: 90%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: modalFadeIn 0.3s ease-out;
  }

  @keyframes modalFadeIn {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem;
    background: linear-gradient(135deg, #ffb400 0%, #f0a800 100%);
    color: white;
  }

  .modal-header h3 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
  }

  .modal-close {
    background: none;
    border: none;
    color: white;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: background-color 0.2s ease;
  }

  .modal-close:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }

  .modal-body {
    padding: 2rem;
    overflow-y: auto;
    flex-grow: 1;
  }

  .form-group {
    margin-bottom: 1.5rem;
  }

  .form-group label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #374151;
    font-size: 0.875rem;
  }

  .required {
    color: #ef4444;
    font-weight: 600;
  }

  .form-input {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 0.875rem;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    background-color: #fff;
    box-sizing: border-box;
  }

  .form-input:focus {
    outline: none;
    border-color: #ffb400;
    box-shadow: 0 0 0 3px rgba(255, 180, 0, 0.1);
  }

  .form-input.error {
    border-color: #ef4444;
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
  }

  .field-error {
    margin-top: 0.25rem;
    font-size: 0.75rem;
    color: #ef4444;
    font-weight: 500;
  }

  .info-section {
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    border-top: 1px solid #e5e7eb;
  }

  .info-card {
    background: #fffbeb;
    border: 1px solid #fed7aa;
    border-radius: 8px;
    padding: 1.25rem;
  }

  .info-card h4 {
    color: #d97706;
    font-size: 0.875rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .info-card p {
    color: #92400e;
    font-size: 0.875rem;
    line-height: 1.5;
    margin: 0;
  }

  .modal-footer {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    padding: 1.5rem;
    background-color: #f8fafc;
    border-top: 1px solid #e2e8f0;
  }

  .modal-footer :global(button) {
    flex: 1;
    max-width: calc(50% - 0.5rem);
  }

  @media (max-width: 640px) {
    .modal-content {
      width: 95%;
      max-height: 95%;
    }
    
    .modal-body,
    .modal-footer {
      padding: 1.5rem;
    }
  }
</style> 